import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
    collection, 
    query, 
    where,
    orderBy, 
    onSnapshot, 
    addDoc,
    setDoc,
    getDocs,
    updateDoc, 
    deleteDoc, 
    doc,
    getDoc,
    serverTimestamp 
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import './Missions.css';

const Missions = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();
    const [missions, setMissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingMission, setEditingMission] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [missionStats, setMissionStats] = useState({});
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        location: '',
        leader: '',
        status: 'planned',
        priority: 'medium',
        startDate: '',
        endDate: '',
        description: '',
        imageUrl: '',
        imageFile: null
    });
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        loadMissions();
    }, [statusFilter]);

    const loadMissions = () => {
        setLoading(true);
        let missionsQuery;
        
        if (statusFilter !== 'all') {
            missionsQuery = query(
                collection(db, 'missions'),
                where('status', '==', statusFilter),
                orderBy('startDate', 'desc')
            );
        } else {
            missionsQuery = query(
                collection(db, 'missions'),
                orderBy('startDate', 'desc')
            );
        }

        const unsubscribe = onSnapshot(missionsQuery, (snapshot) => {
            const missionsData = [];
            snapshot.forEach((doc) => {
                missionsData.push({ id: doc.id, ...doc.data() });
            });
            setMissions(missionsData);
            setLoading(false);
        }, (error) => {
            console.error('Error loading missions:', error);
            showToast('Error loading missions', 'error');
            setLoading(false);
        });

        return () => unsubscribe();
    };

    // Track views and commitments for each mission
    useEffect(() => {
        if (missions.length === 0) return;

        const unsubscribes = [];

        missions.forEach(mission => {
            // Views listener
            const viewsRef = collection(db, 'missions', mission.id, 'views');
            const unsubViews = onSnapshot(viewsRef, (snap) => {
                setMissionStats(prev => ({
                    ...prev,
                    [mission.id]: {
                        ...prev[mission.id],
                        views: snap.size
                    }
                }));
            });

            // Commitments listener
            const commitQuery = query(
                collection(db, 'commitments'),
                where('missionId', '==', mission.id)
            );

            const unsubCommits = onSnapshot(commitQuery, (snap) => {
                setMissionStats(prev => ({
                    ...prev,
                    [mission.id]: {
                        ...prev[mission.id],
                        commits: snap.size
                    }
                }));
            });

            unsubscribes.push(unsubViews, unsubCommits);
        });

        return () => unsubscribes.forEach(unsub => unsub && unsub());
    }, [missions]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showToast('Please select an image file', 'error');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                showToast('Image size must be less than 5MB', 'error');
                return;
            }

            setFormData(prev => ({ ...prev, imageFile: file }));
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadToCloudinary = async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
            formData.append('cloud_name', process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);
            formData.append('folder', 'mission-images');

            const response = await fetch(
                `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
                {
                    method: 'POST',
                    body: formData
                }
            );

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            return data.secure_url;
        } catch (error) {
            console.error('Error uploading to Cloudinary:', error);
            throw new Error('Failed to upload image to Cloudinary');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            category: '',
            location: '',
            leader: '',
            status: 'planned',
            priority: 'medium',
            startDate: '',
            endDate: '',
            description: '',
            imageUrl: '',
            imageFile: null
        });
        setImagePreview('');
        setEditingMission(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.location || !formData.leader || !formData.startDate || !formData.endDate || !formData.description) {
            showToast('Please fill in all required fields', 'error');
            return;
        }

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            showToast('End date must be after start date', 'error');
            return;
        }

        setLoading(true);

        try {
            let imageUrl = formData.imageUrl;

            // Upload to Cloudinary if there's a file
            if (formData.imageFile) {
                imageUrl = await uploadToCloudinary(formData.imageFile);
            }

            const missionData = {
                title: formData.title,
                category: formData.category,
                location: formData.location,
                leader: formData.leader,
                status: formData.status,
                priority: formData.priority,
                startDate: formData.startDate,
                endDate: formData.endDate,
                description: formData.description,
                imageUrl: imageUrl || '',
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid
            };

            if (editingMission) {
                // Update existing mission
                await updateDoc(doc(db, 'missions', editingMission.id), missionData);
                showToast('Mission updated successfully!', 'success');
            } else {
                // Create new mission
                missionData.createdAt = serverTimestamp();
                missionData.createdBy = currentUser.uid;
                await addDoc(collection(db, 'missions'), missionData);
                showToast('Mission created successfully!', 'success');
            }

            setShowForm(false);
            resetForm();
        } catch (error) {
            console.error('Error saving mission:', error);
            showToast(error.message || 'Error saving mission', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (mission) => {
        setEditingMission(mission);
        setFormData({
            title: mission.title || '',
            category: mission.category || '',
            location: mission.location || '',
            leader: mission.leader || '',
            status: mission.status || 'planned',
            priority: mission.priority || 'medium',
            startDate: mission.startDate || '',
            endDate: mission.endDate || '',
            description: mission.description || '',
            imageUrl: mission.imageUrl || '',
            imageFile: null
        });
        setImagePreview(mission.imageUrl || '');
        setShowForm(true);
    };

    const handleDelete = async (missionId) => {
        const result = await Swal.fire({
            title: 'Delete Mission?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'missions', missionId));
                showToast('Mission deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting mission:', error);
                showToast('Error deleting mission', 'error');
            }
        }
    };

    const handleCommit = async (missionId) => {
        try {
            // Check if already committed
            const q = query(
                collection(db, 'commitments'),
                where('missionId', '==', missionId),
                where('userId', '==', currentUser.uid)
            );
            
            const existingCommit = await getDocs(q);
            
            if (!existingCommit.empty) {
                showToast('You have already committed to this mission', 'warning');
                return;
            }

            await addDoc(collection(db, 'commitments'), {
                missionId,
                userId: currentUser.uid,
                userEmail: currentUser.email,
                userName: userData?.fullName || 'Unknown',
                committedAt: serverTimestamp()
            });

            showToast('Thank you for committing to this mission!', 'success');
        } catch (error) {
            console.error('Error committing to mission:', error);
            showToast('Error committing to mission', 'error');
        }
    };

    const handleUncommit = async (missionId) => {
        try {
            const q = query(
                collection(db, 'commitments'),
                where('missionId', '==', missionId),
                where('userId', '==', currentUser.uid)
            );

            const snapshot = await getDocs(q);

            const deletePromises = snapshot.docs.map(docItem => 
                deleteDoc(doc(db, 'commitments', docItem.id))
            );
            
            await Promise.all(deletePromises);

            showToast('Commitment cancelled', 'success');
        } catch (error) {
            console.error('Error uncommitting from mission:', error);
            showToast('Error cancelling commitment', 'error');
        }
    };

    const trackView = async (missionId) => {
        if (!currentUser) return;
        
        try {
            const viewRef = doc(db, 'missions', missionId, 'views', currentUser.uid);
            const viewSnap = await getDoc(viewRef);

            if (!viewSnap.exists()) {
                await setDoc(viewRef, {
                    userId: currentUser.uid,
                    viewedAt: serverTimestamp()
                });
            }
        } catch (error) {
            console.error('Error tracking view:', error);
        }
    };

    const calculateProgress = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const now = new Date();

        if (now < start) return 0;
        if (now > end) return 100;

        const total = end - start;
        const elapsed = now - start;
        return Math.round((elapsed / total) * 100);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const filteredMissions = missions.filter(mission => {
        const searchableText = `${mission.title} ${mission.location} ${mission.leader} ${mission.description} ${mission.category || ''}`.toLowerCase();
        return searchableText.includes(searchTerm.toLowerCase());
    });

    const stats = {
        total: missions.length,
        active: missions.filter(m => m.status === 'active').length,
        planned: missions.filter(m => m.status === 'planned').length,
        completed: missions.filter(m => m.status === 'completed').length
    };

    if (loading && missions.length === 0) {
        return (
            <div className="text-center" style={{ padding: '50px' }}>
                <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#1034A6' }}></i>
                <p style={{ marginTop: '20px' }}>Loading missions...</p>
            </div>
        );
    }

    return (
        <div className="missions-page">
            <div className="missions-header">
                <div>
                    <h1 className="missions-title">Ministry Missions</h1>
                    <p className="missions-subtitle">Join us in spreading hope and making a difference</p>
                </div>
                <div className="missions-controls">
                    <div className="missions-search">
                        <input
                            type="text"
                            placeholder="Search missions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="missions-search-input"
                        />
                        <i className="fas fa-search missions-search-icon"></i>
                    </div>
                    <select
                        className="missions-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Missions</option>
                        <option value="planned">Planned</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                    </select>
                    {isAdmin && (
                        <button
                            className="missions-add-btn"
                            onClick={() => {
                                resetForm();
                                setShowForm(!showForm);
                            }}
                        >
                            <i className="fas fa-plus"></i>
                            {showForm ? 'Cancel' : 'Add Mission'}
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="missions-stats">
                <div className="missions-stat-card">
                    <div className="missions-stat-icon total">
                        <i className="fas fa-bullseye"></i>
                    </div>
                    <div>
                        <div className="missions-stat-value">{stats.total}</div>
                        <div className="missions-stat-label">Total Missions</div>
                    </div>
                </div>
                <div className="missions-stat-card">
                    <div className="missions-stat-icon active">
                        <i className="fas fa-play-circle"></i>
                    </div>
                    <div>
                        <div className="missions-stat-value">{stats.active}</div>
                        <div className="missions-stat-label">Active</div>
                    </div>
                </div>
                <div className="missions-stat-card">
                    <div className="missions-stat-icon planned">
                        <i className="fas fa-clock"></i>
                    </div>
                    <div>
                        <div className="missions-stat-value">{stats.planned}</div>
                        <div className="missions-stat-label">Planned</div>
                    </div>
                </div>
                <div className="missions-stat-card">
                    <div className="missions-stat-icon completed">
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <div>
                        <div className="missions-stat-value">{stats.completed}</div>
                        <div className="missions-stat-label">Completed</div>
                    </div>
                </div>
            </div>

            {/* Mission Form */}
            {showForm && isAdmin && (
                <div className="missions-form-container">
                    <h2 className="missions-form-title">
                        {editingMission ? 'Edit Mission' : 'Create New Mission'}
                    </h2>
                    <form onSubmit={handleSubmit} className="missions-form">
                        <div className="missions-form-grid">
                            <div className="missions-form-group">
                                <label htmlFor="title">Mission Title *</label>
                                <input
                                    type="text"
                                    id="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="category">Category</label>
                                <select
                                    id="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Category</option>
                                    <option value="evangelism">Evangelism</option>
                                    <option value="humanitarian">Humanitarian Aid</option>
                                    <option value="discipleship">Discipleship</option>
                                    <option value="medical">Medical Mission</option>
                                    <option value="construction">Construction</option>
                                    <option value="education">Education</option>
                                </select>
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="location">Location *</label>
                                <input
                                    type="text"
                                    id="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="leader">Mission Leader *</label>
                                <input
                                    type="text"
                                    id="leader"
                                    value={formData.leader}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="status">Status *</label>
                                <select
                                    id="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="planned">Planned</option>
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="priority">Priority</label>
                                <select
                                    id="priority"
                                    value={formData.priority}
                                    onChange={handleInputChange}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="startDate">Start Date *</label>
                                <input
                                    type="date"
                                    id="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="endDate">End Date *</label>
                                <input
                                    type="date"
                                    id="endDate"
                                    value={formData.endDate}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="missions-form-group full-width">
                                <label htmlFor="description">Description *</label>
                                <textarea
                                    id="description"
                                    rows="4"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                ></textarea>
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="imageFile">Upload Image</label>
                                <input
                                    type="file"
                                    id="imageFile"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {imagePreview && (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="missions-image-preview"
                                    />
                                )}
                            </div>

                            <div className="missions-form-group">
                                <label htmlFor="imageUrl">Or Image URL</label>
                                <input
                                    type="url"
                                    id="imageUrl"
                                    value={formData.imageUrl}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                />
                            </div>
                        </div>

                        <div className="missions-form-actions">
                            <button
                                type="button"
                                className="missions-btn-secondary"
                                onClick={() => {
                                    setShowForm(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="missions-btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i>
                                        {editingMission ? 'Update Mission' : 'Create Mission'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Missions Grid */}
            <div className="missions-grid">
                {filteredMissions.length === 0 ? (
                    <div className="missions-empty">
                        <i className="fas fa-hands-helping"></i>
                        <h3>No Missions Found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                ) : (
                    filteredMissions.map(mission => {
                        const hasCommitted = missionStats[mission.id]?.userCommitted || false;
                        const progress = mission.status === 'active' 
                            ? calculateProgress(mission.startDate, mission.endDate) 
                            : 0;

                        return (
                            <div
                                key={mission.id}
                                className={`missions-card priority-${mission.priority}`}
                                onClick={() => trackView(mission.id)}
                            >
                                <div className="missions-image-container">
                                    {mission.imageUrl ? (
                                        <img
                                            src={mission.imageUrl}
                                            alt={mission.title}
                                            className="missions-image"
                                        />
                                    ) : (
                                        <div className="missions-no-image">
                                            <i className="fas fa-hands-helping"></i>
                                        </div>
                                    )}
                                    <span className={`missions-status-badge status-${mission.status}`}>
                                        {mission.status}
                                    </span>
                                </div>

                                <div className="missions-content">
                                    {mission.category && (
                                        <div className="missions-category">{mission.category}</div>
                                    )}
                                    <h3 className="missions-title">{mission.title}</h3>

                                    <div className="missions-details">
                                        <div className="missions-detail">
                                            <i className="fas fa-map-marker-alt"></i>
                                            <span>{mission.location}</span>
                                        </div>
                                        <div className="missions-detail">
                                            <i className="fas fa-calendar-alt"></i>
                                            <span>
                                                {formatDate(mission.startDate)} - {formatDate(mission.endDate)}
                                            </span>
                                        </div>
                                        <div className="missions-detail">
                                            <i className="fas fa-user"></i>
                                            <span>Leader: {mission.leader}</span>
                                        </div>
                                    </div>

                                    {mission.status === 'active' && (
                                        <div className="missions-progress">
                                            <div className="missions-progress-info">
                                                <span>Progress</span>
                                                <span>{progress}%</span>
                                            </div>
                                            <div className="missions-progress-bar">
                                                <div
                                                    className="missions-progress-fill"
                                                    style={{ width: `${progress}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}

                                    <p className="missions-description">{mission.description}</p>

                                    <div className="missions-stats">
                                        <div className="missions-stat-item">
                                            <i className="fas fa-eye"></i>
                                            <span>{missionStats[mission.id]?.views || 0} views</span>
                                        </div>
                                        <div className="missions-stat-item">
                                            <i className="fas fa-users"></i>
                                            <span>{missionStats[mission.id]?.commits || 0} committed</span>
                                        </div>
                                    </div>

                                    <div className="missions-actions">
                                        <button
                                            className="missions-support-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.location.href = '/payment';
                                            }}
                                        >
                                            <i className="fas fa-handshake"></i> Support
                                        </button>

                                        <button
                                            className={`missions-commit-btn ${hasCommitted ? 'committed' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (hasCommitted) {
                                                    handleUncommit(mission.id);
                                                } else {
                                                    handleCommit(mission.id);
                                                }
                                            }}
                                            disabled={mission.status === 'completed'}
                                        >
                                            <i className="fas fa-check-circle"></i> 
                                            {hasCommitted ? 'Committed' : 'I Will Go'}
                                        </button>

                                        {isAdmin && (
                                            <>
                                                <button
                                                    className="missions-edit-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(mission);
                                                    }}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="missions-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(mission.id);
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default Missions;
